import { IMedia } from '@dtos/Course'
import { rememberPlayback, resolvePlaybackStart } from '@utils/playbackMemory'
import { buildSparkVideosPlayerUrl } from '@utils/sparkVideosPlayerUrl'
import { CSSProperties, useEffect, useRef, useState } from 'react'

interface ISparkVideosPlayerIframeProps {
  media: IMedia
  /** Posição salva no progresso da aula, em segundos. */
  startMediaFrom?: number
  style?: CSSProperties
}

/**
 * Player do Spark Videos no web.
 *
 * Retoma de onde o aluno estava: na primeira abertura pelo progresso salvo e,
 * se o player for montado de novo nesta aba, pela última posição que ele mesmo
 * informou (quem estava tocando volta tocando). A URL é calculada uma vez por
 * montagem: o progresso salvo continua mudando durante a reprodução, e trocar o
 * `src` com o vídeo tocando recarregaria o iframe.
 */
export function SparkVideosPlayerIframe({
  media,
  startMediaFrom,
  style,
}: ISparkVideosPlayerIframeProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const mediaKey = String(media.id)
  const [src] = useState(() =>
    buildSparkVideosPlayerUrl(
      media,
      resolvePlaybackStart(mediaKey, startMediaFrom),
    ),
  )

  useEffect(() => {
    // Mesmo protocolo que o useSparkVideosIframeProgress escuta, mas aqui a
    // mensagem precisa vir DESTE iframe: um vídeo de conteúdo complementar
    // aberto por cima da aula também emite `time_update`.
    const handleMessage = (event: MessageEvent) => {
      const frameWindow = iframeRef.current?.contentWindow
      if (!frameWindow || event.source !== frameWindow) return
      const data = event.data
      if (!data || data.cmd !== 'time_update') return
      const currentTime = Number(data.current_time)
      if (!Number.isFinite(currentTime)) return

      // Vídeo que chegou ao fim recomeça do início na próxima montagem.
      const hasEnded = data.state === 'ended'
      rememberPlayback(mediaKey, {
        currentTime: hasEnded ? 0 : currentTime,
        isPlaying: !hasEnded && data.state === 'playing',
      })
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [mediaKey])

  return (
    <iframe
      ref={iframeRef}
      src={src}
      style={style}
      allow="autoplay; fullscreen; picture-in-picture"
      allowFullScreen
    />
  )
}
